'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, {
  Marker,
  MapLayerMouseEvent,
  MapRef,
  MarkerDragEvent,
  LngLatBoundsLike,
} from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { IconMapPin } from '@tabler/icons-react';
import { cn } from 'ui';

const MAPBOX_ACCESS_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN || '';

// Half-size (in degrees) of the box the user is allowed to pan within, around the
// selected locality centroid. ~0.15° ≈ 15 km: enough to cover a city and its
// outskirts while preventing wandering to another town.
const BOUNDS_HALF_DEGREES = 0.15;

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface LocationPickerProps {
  // Currently picked GPS point (null until the user places the pin).
  value: Coordinates | null;
  onChange: (coordinates: Coordinates) => void;
  // Locality centroid used to center the map and constrain panning. When null the
  // picker is inert (no locality chosen yet).
  center: Coordinates | null;
  disabled?: boolean;
  className?: string;
  height?: string;
  // Hint shown before a locality is selected / a pin placed.
  emptyHint?: string;
}

export const LocationPicker = ({
  value,
  onChange,
  center,
  disabled = false,
  className,
  height = '18rem',
  emptyHint,
}: LocationPickerProps) => {
  const [showHint, setShowHint] = useState(!value);
  const mapRef = useRef<MapRef | null>(null);

  // `initialViewState` only applies on mount; when the locality (and thus
  // `center`) changes we must move the camera imperatively so it flies to the new
  // centroid instead of staying on the previous one.
  useEffect(() => {
    if (!center) return;
    mapRef.current?.flyTo({
      center: [center.longitude, center.latitude],
      zoom: 14,
    });
  }, [center]);

  const maxBounds = useMemo<LngLatBoundsLike | undefined>(() => {
    if (!center) return undefined;
    return [
      [
        center.longitude - BOUNDS_HALF_DEGREES,
        center.latitude - BOUNDS_HALF_DEGREES,
      ],
      [
        center.longitude + BOUNDS_HALF_DEGREES,
        center.latitude + BOUNDS_HALF_DEGREES,
      ],
    ];
  }, [center]);

  const initialViewState = useMemo(() => {
    const focus = value ?? center;
    if (!focus) return undefined;
    return { latitude: focus.latitude, longitude: focus.longitude, zoom: 14 };
  }, [value, center]);

  const handleClick = useCallback(
    (event: MapLayerMouseEvent) => {
      if (disabled) return;
      setShowHint(false);
      onChange({ latitude: event.lngLat.lat, longitude: event.lngLat.lng });
    },
    [disabled, onChange],
  );

  const handleMarkerDragEnd = useCallback(
    (event: MarkerDragEvent) => {
      onChange({ latitude: event.lngLat.lat, longitude: event.lngLat.lng });
    },
    [onChange],
  );

  if (disabled || !center || !initialViewState) {
    return (
      <div
        className={cn(
          'rounded-md border bg-muted flex flex-col items-center justify-center text-center gap-2 p-4',
          className,
        )}
        style={{ height }}
      >
        <IconMapPin className='h-8 w-8 text-muted-foreground' />
        {emptyHint && (
          <p className='text-sm text-muted-foreground'>{emptyHint}</p>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn('relative rounded-md overflow-hidden border', className)}
      style={{ height }}
    >
      <Map
        ref={mapRef}
        initialViewState={initialViewState}
        maxBounds={maxBounds}
        minZoom={11}
        onClick={handleClick}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        style={{ width: '100%', height: '100%' }}
        mapStyle='mapbox://styles/mapbox/standard'
        attributionControl={false}
        cursor='crosshair'
      >
        {value && (
          <Marker
            longitude={value.longitude}
            latitude={value.latitude}
            color='#FC762D'
            draggable
            onDragEnd={handleMarkerDragEnd}
          />
        )}
      </Map>

      {showHint && !value && emptyHint && (
        <div className='absolute inset-x-0 bottom-0 bg-black/60 text-white text-xs px-3 py-2 text-center'>
          {emptyHint}
        </div>
      )}
    </div>
  );
};
